import { useField, useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';

import { FormField } from '../FormField';
import { Input, InputProps } from 'theSource/components/Input';

export interface TextFieldProps
  extends Omit<InputProps, 'error' | 'onChange' | 'value'> {
  autocomplete?: HTMLInputElement['autocomplete'];
  externalError?: boolean;
  helperText?: string;
  label: string;
  name: string;
  required?: boolean;
  testId?: string;
}

/**
 * TextField component that integrates with Formik for form handling.
 * Provides input functionality with error states, helper text, and accessibility features.
 */
export const TextField = ({
  disabled,
  fullWidth = true,
  helperText,
  id,
  label,
  name,
  required,
  testId = 'text-field',
  type,
  externalError,
  autocomplete,

  ...props
}: TextFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [field, { error, touched }] = useField({
    name,
    type,
  });
  const { isSubmitting } = useFormikContext();

  const invalid = useMemo(() => touched && Boolean(error), [error, touched]);
  const effectiveType =
    type === 'password' ? (showPassword ? 'text' : 'password') : type;

  return (
    <FormField
      disabled={disabled || isSubmitting}
      error={invalid || externalError}
      fullWidth={fullWidth}
      helperText={invalid ? error : helperText}
      inputId={id}
      label={label}
      required={required}
      testId={testId}
    >
      <Input
        id={id}
        type={effectiveType}
        {...field}
        {...props}
        inputProps={{
          autoComplete: autocomplete, // Pass autocomplete via inputProps
          ...props.inputProps, // Spread any additional inputProps
        }}
        endAdornment={
          type === 'password' ? (
            <InputAdornment
              position="end"
              variant="outlined"
              classeName="password-toggle"
            >
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <i className="ph ph-eye-slash" aria-hidden="true" />
                ) : (
                  <i className="ph ph-eye" aria-hidden="true" />
                )}
              </button>
            </InputAdornment>
          ) : undefined
        }
      />
    </FormField>
  );
};
